import React, { useState, useEffect, useCallback } from 'react';
import { 
  billingDateService, 
  BillingDifference, 
  BillingDifferencesResponse,
  BillingDateFixResponse,
  InvoiceLineUpdate,
  BillingDifferencesSummary,
  BillingDifferencesStatistics
} from '../services/billingDateService';

interface BillingDateManagerProps {
  onClose?: () => void;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

const BillingDateManager: React.FC<BillingDateManagerProps> = ({
  onClose,
  onError,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [differences, setDifferences] = useState<BillingDifference[]>([]);
  const [summary, setSummary] = useState<{
    success: boolean;
    summary: BillingDifferencesSummary;
    statistics: {
      discrepancy_types: Record<string, number>;
      status_breakdown: Record<string, {
        lines: number;
        differences: number;
      }>;
    };
    filters: {
      include_deleted: boolean;
    };
  } | null>(null);
  const [selectedDifferences, setSelectedDifferences] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    includeDeleted: false,
    statusFilter: [] as string[],
    discrepancyTypeFilter: [] as string[],
    limit: 100
  });
  const [activeTab, setActiveTab] = useState<'overview' | 'differences' | 'bulk-fix'>('overview');
  const [fixMode, setFixMode] = useState<'preview' | 'apply'>('preview');
  const [fixResults, setFixResults] = useState<BillingDateFixResponse | null>(null);
  const [bulkUpdateProgress, setBulkUpdateProgress] = useState<{
    total: number;
    completed: number;
    failed: number;
    inProgress: boolean;
  }>({ total: 0, completed: 0, failed: 0, inProgress: false });

  // Load data on component mount
  useEffect(() => {
    loadOverview();
  }, []);

  // Load differences when filters change
  useEffect(() => {
    if (activeTab === 'differences') {
      loadDifferences();
    }
  }, [filters, activeTab]);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const summaryData = await billingDateService.getBillingDifferencesSummary(filters.includeDeleted);
      setSummary(summaryData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load overview';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadDifferences = async () => {
    try {
      setLoading(true);
      const response = await billingDateService.getBillingDifferences(
        filters.includeDeleted,
        filters.statusFilter.length > 0 ? filters.statusFilter : undefined,
        filters.limit
      );
      
      let filteredDifferences = response.differences;
      
      // Apply discrepancy type filter
      if (filters.discrepancyTypeFilter.length > 0) {
        filteredDifferences = filteredDifferences.filter(diff => 
          filters.discrepancyTypeFilter.includes(diff.discrepancy_type)
        );
      }
      
      setDifferences(filteredDifferences);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load differences';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDifference = (invoiceLineId: string, selected: boolean) => {
    const newSelected = new Set(selectedDifferences);
    if (selected) {
      newSelected.add(invoiceLineId);
    } else {
      newSelected.delete(invoiceLineId);
    }
    setSelectedDifferences(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allIds = differences.map(diff => diff.invoiceline_id);
      setSelectedDifferences(new Set(allIds));
    } else {
      setSelectedDifferences(new Set());
    }
  };

  const handleBulkFix = async () => {
    if (selectedDifferences.size === 0) {
      onError?.('Please select at least one difference to fix');
      return;
    }

    try {
      setBulkUpdateProgress({
        total: selectedDifferences.size,
        completed: 0,
        failed: 0,
        inProgress: true
      });

      const updates: InvoiceLineUpdate[] = [];
      
      for (const invoiceLineId of selectedDifferences) {
        const difference = differences.find(d => d.invoiceline_id === invoiceLineId);
        if (difference) {
          updates.push({
            invoiceline_id: invoiceLineId,
            line_billing_end: difference.billing_period.expected_end,
            invoiceline_updatedby: 'billing_date_manager'
          });
        }
      }

      const result = await billingDateService.bulkUpdateInvoiceLineBillingDates(updates);
      
      setBulkUpdateProgress(prev => ({
        ...prev,
        completed: result.updated_count,
        failed: result.failed_count,
        inProgress: false
      }));

      if (result.success && result.updated_count > 0) {
        onSuccess?.(`Successfully updated ${result.updated_count} invoice lines`);
        // Reload data
        await loadDifferences();
        await loadOverview();
        // Clear selection
        setSelectedDifferences(new Set());
      }

      if (result.failed_count > 0) {
        onError?.(`${result.failed_count} updates failed. Check the results for details.`);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update billing dates';
      onError?.(errorMessage);
      setBulkUpdateProgress(prev => ({ ...prev, inProgress: false }));
    }
  };

  const handleSystemWideFix = async () => {
    try {
      setLoading(true);
      const result = await billingDateService.checkAndFixBillingDates(fixMode === 'apply');
      setFixResults(result);
      
      if (result.success && fixMode === 'apply' && result.total_lines_fixed > 0) {
        onSuccess?.(`Successfully fixed ${result.total_lines_fixed} billing date issues`);
        // Reload data
        await loadDifferences();
        await loadOverview();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to run system-wide fix';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderOverviewTab = () => (
    <div className="row">
      <div className="col-md-8">
        {summary && (
          <>
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-primary">{summary.summary.total_invoices_checked}</h5>
                    <p className="card-text">Invoices Checked</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-info">{summary.summary.total_lines_checked}</h5>
                    <p className="card-text">Lines Checked</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-warning">{summary.summary.differences_found}</h5>
                    <p className="card-text">Differences Found</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-danger">{summary.summary.discrepancy_rate}%</h5>
                    <p className="card-text">Error Rate</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5>Discrepancy Types</h5>
              </div>
              <div className="card-body">
                {Object.entries(summary.statistics.discrepancy_types).map(([type, count]) => (
                  <div key={type} className="d-flex justify-content-between align-items-center mb-2">
                    <span className="badge bg-secondary">{type.replace(/_/g, ' ')}</span>
                    <span className="fw-bold">{String(count)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h5>Status Breakdown</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Lines</th>
                        <th>Differences</th>
                        <th>Error Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(summary.statistics.status_breakdown).map(([status, data]: [string, any]) => (
                        <tr key={status}>
                          <td>
                            <span className={`badge ${
                              status === 'FINALIZED' ? 'bg-success' : 
                              status === 'DRAFT' ? 'bg-warning' : 'bg-secondary'
                            }`}>
                              {status}
                            </span>
                          </td>
                          <td>{String(data.lines)}</td>
                          <td>{String(data.differences)}</td>
                          <td>{data.lines > 0 ? ((data.differences / data.lines) * 100).toFixed(1) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="col-md-4">
        <div className="card">
          <div className="card-header">
            <h5>Quick Actions</h5>
          </div>
          <div className="card-body">
            <button
              className="btn btn-primary w-100 mb-2"
              onClick={() => setActiveTab('differences')}
            >
              View All Differences
            </button>
            <button
              className="btn btn-warning w-100 mb-2"
              onClick={() => setActiveTab('bulk-fix')}
            >
              System-Wide Fix
            </button>
            <button
              className="btn btn-outline-secondary w-100"
              onClick={loadOverview}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDifferencesTab = () => (
    <div>
      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header">
          <h5>Filters</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="includeDeleted"
                  checked={filters.includeDeleted}
                  onChange={(e) => setFilters(prev => ({ ...prev, includeDeleted: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="includeDeleted">
                  Include Deleted
                </label>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status Filter</label>
              <select
                className="form-select"
                multiple
                value={filters.statusFilter}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions, option => option.value);
                  setFilters(prev => ({ ...prev, statusFilter: values }));
                }}
              >
                <option value="DRAFT">Draft</option>
                <option value="FINALIZED">Finalized</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="PAID">Paid</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Discrepancy Type</label>
              <select
                className="form-select"
                multiple
                value={filters.discrepancyTypeFilter}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions, option => option.value);
                  setFilters(prev => ({ ...prev, discrepancyTypeFilter: values }));
                }}
              >
                <option value="OFF_BY_ONE_ERROR">Off by One Error</option>
                <option value="TOO_LONG">Too Long</option>
                <option value="TOO_SHORT">Too Short</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Limit</label>
              <select
                className="form-select"
                value={filters.limit}
                onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5>Bulk Actions</h5>
          <div>
            <button
              className="btn btn-outline-primary btn-sm me-2"
              onClick={() => handleSelectAll(true)}
              disabled={differences.length === 0}
            >
              Select All
            </button>
            <button
              className="btn btn-outline-secondary btn-sm me-2"
              onClick={() => handleSelectAll(false)}
              disabled={selectedDifferences.size === 0}
            >
              Clear Selection
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={handleBulkFix}
              disabled={selectedDifferences.size === 0 || bulkUpdateProgress.inProgress}
            >
              {bulkUpdateProgress.inProgress ? 'Updating...' : `Fix Selected (${selectedDifferences.size})`}
            </button>
          </div>
        </div>
        {bulkUpdateProgress.total > 0 && (
          <div className="card-body">
            <div className="progress mb-2">
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${((bulkUpdateProgress.completed + bulkUpdateProgress.failed) / bulkUpdateProgress.total) * 100}%` }}
              >
                {bulkUpdateProgress.completed + bulkUpdateProgress.failed} / {bulkUpdateProgress.total}
              </div>
            </div>
            <small className="text-muted">
              Completed: {bulkUpdateProgress.completed}, Failed: {bulkUpdateProgress.failed}
            </small>
          </div>
        )}
      </div>

      {/* Differences Table */}
      <div className="card">
        <div className="card-header">
          <h5>Billing Date Differences ({differences.length})</h5>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : differences.length === 0 ? (
            <div className="text-center text-muted">
              <p>No billing date differences found.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: '30px' }}>
                      <input
                        type="checkbox"
                        checked={selectedDifferences.size === differences.length && differences.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th>Invoice</th>
                    <th>Line</th>
                    <th>Status</th>
                    <th>Billing Cycle</th>
                    <th>Current Period</th>
                    <th>Expected Period</th>
                    <th>Difference</th>
                    <th>Amount</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {differences.map((diff) => (
                    <tr key={diff.invoiceline_id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedDifferences.has(diff.invoiceline_id)}
                          onChange={(e) => handleSelectDifference(diff.invoiceline_id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <div>
                          <strong>{diff.invoice_name}</strong>
                          <br />
                          <small className="text-muted">{billingDateService.formatDate(diff.invoice_date)}</small>
                        </div>
                      </td>
                      <td>{diff.invoiceline_name}</td>
                      <td>
                        <span className={`badge ${
                          diff.invoice_status === 'FINALIZED' ? 'bg-success' : 
                          diff.invoice_status === 'DRAFT' ? 'bg-warning' : 'bg-secondary'
                        }`}>
                          {diff.invoice_status}
                        </span>
                      </td>
                      <td>{diff.billing_cycle}</td>
                      <td>
                        <div>
                          <small>{billingDateService.formatDate(diff.billing_period.start)}</small>
                          <br />
                          <small>to {billingDateService.formatDate(diff.billing_period.actual_end)}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <small>{billingDateService.formatDate(diff.billing_period.start)}</small>
                          <br />
                          <small>to {billingDateService.formatDate(diff.billing_period.expected_end)}</small>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          diff.difference_days > 0 ? 'bg-danger' : 'bg-warning'
                        }`}>
                          {diff.difference_days > 0 ? '+' : ''}{diff.difference_days} days
                        </span>
                      </td>
                      <td>{billingDateService.formatCurrency(diff.line_total, diff.currency)}</td>
                      <td>
                        <span className={`badge ${
                          diff.discrepancy_type === 'OFF_BY_ONE_ERROR' ? 'bg-warning' :
                          diff.discrepancy_type === 'TOO_LONG' ? 'bg-danger' : 'bg-info'
                        }`}>
                          {diff.discrepancy_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderBulkFixTab = () => (
    <div>
      <div className="card mb-4">
        <div className="card-header">
          <h5>System-Wide Billing Date Fix</h5>
        </div>
        <div className="card-body">
          <div className="alert alert-info">
            <strong>System-Wide Fix</strong><br />
            This will check all finalized invoices and fix billing end dates where they don't match the expected calculation:
            <code>billing_end = billing_start + billing_period - 1 day</code>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="fixMode"
                  id="preview"
                  value="preview"
                  checked={fixMode === 'preview'}
                  onChange={(e) => setFixMode(e.target.value as 'preview' | 'apply')}
                />
                <label className="form-check-label" htmlFor="preview">
                  <strong>Preview Mode</strong> - Show what would be fixed without making changes
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="fixMode"
                  id="apply"
                  value="apply"
                  checked={fixMode === 'apply'}
                  onChange={(e) => setFixMode(e.target.value as 'preview' | 'apply')}
                />
                <label className="form-check-label" htmlFor="apply">
                  <strong>Apply Mode</strong> - Actually fix the billing dates
                </label>
              </div>
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleSystemWideFix}
            disabled={loading}
          >
            {loading ? 'Processing...' : `Run ${fixMode === 'preview' ? 'Preview' : 'Fix'}`}
          </button>
        </div>
      </div>

      {fixResults && (
        <div className="card">
          <div className="card-header">
            <h5>Fix Results</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-3">
                <div className="text-center">
                  <h6 className="text-primary">{fixResults.total_invoices_checked}</h6>
                  <small>Invoices Checked</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center">
                  <h6 className="text-info">{fixResults.total_lines_checked}</h6>
                  <small>Lines Checked</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center">
                  <h6 className="text-warning">{fixResults.total_lines_fixed}</h6>
                  <small>Lines {fixMode === 'preview' ? 'Would Be' : ''} Fixed</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center">
                  <h6 className="text-success">{fixResults.invoices_with_fixes}</h6>
                  <small>Invoices Affected</small>
                </div>
              </div>
            </div>

            <div className="alert alert-info">
              <strong>Mode:</strong> {fixResults.mode}
            </div>

            {fixResults.errors && fixResults.errors.length > 0 && (
              <div className="alert alert-danger">
                <strong>Errors:</strong>
                <ul className="mb-0">
                  {fixResults.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {fixResults.invoices.length > 0 && (
              <div className="mt-3">
                <h6>Affected Invoices:</h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Status</th>
                        <th>Lines Checked</th>
                        <th>Lines Fixed</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fixResults.invoices.slice(0, 10).map((invoice) => (
                        <tr key={invoice.invoice_id}>
                          <td>{invoice.invoice_name}</td>
                          <td>
                            <span className={`badge ${
                              invoice.invoice_status === 'FINALIZED' ? 'bg-success' : 'bg-secondary'
                            }`}>
                              {invoice.invoice_status}
                            </span>
                          </td>
                          <td>{invoice.lines_checked}</td>
                          <td>{invoice.lines_fixed}</td>
                          <td>{invoice.lines_with_errors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {fixResults.invoices.length > 10 && (
                    <small className="text-muted">
                      Showing first 10 of {fixResults.invoices.length} invoices
                    </small>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="billing-date-manager">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Billing Date Manager</h2>
        {onClose && (
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        )}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'differences' ? 'active' : ''}`}
            onClick={() => setActiveTab('differences')}
          >
            Differences ({differences.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'bulk-fix' ? 'active' : ''}`}
            onClick={() => setActiveTab('bulk-fix')}
          >
            System-Wide Fix
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'differences' && renderDifferencesTab()}
        {activeTab === 'bulk-fix' && renderBulkFixTab()}
      </div>
    </div>
  );
};

export default BillingDateManager;